import { useState, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
} from '@mui/material';

import FormatHeading from 'components/CustomIcons/FormatHeading';
import { getNodeType } from 'components/TableOfContents/functions';
import { useEditorBubbleMenu } from 'components/EditorBubbleMenu/context';

import { commonStyles } from './styles';

interface HeadingItem {
  id: string;
  textContent: string;
  editor: any;
  node: {
    type: {
      name: string;
    };
    attrs: any;
  };
  dom: any;
  pos: number;
}

const LinkHeadingsList = () => {
  const { editor, bubbleCleanup, removeAnchor } = useEditorBubbleMenu();
  const [headings, setHeadings] = useState<HeadingItem[]>([]);

  useEffect(() => {
    const content = editor?.storage?.tableOfContents?.content;
    if (!editor || !content) return;

    const filteredHeadings = content.filter(
      (item: HeadingItem) =>
        item.editor &&
        getNodeType(item.node.type.name, item.dom, item.node.attrs, item.pos) ===
          'heading',
    );

    setHeadings(filteredHeadings);
  }, [editor]);

  if (!editor) return null;

  const handleHeadingClick = (headingId: string) => {
    const linkHref = editor.getAttributes('link').href;
    removeAnchor(linkHref);

    // Set link with additional attributes to prevent new tab behavior
    const heading = headings.find((heading) => heading.id === headingId);
    const headingPos = heading?.pos;

    if (headingPos === undefined) {
      return;
    }

    const headingTextContentRaw = heading?.textContent.trim();

    const headingTextContent = headingTextContentRaw
      ? headingTextContentRaw
          .replace(/[^\w\s]/g, '') // Remove all symbols/punctuation except alphanumeric and spaces
          .replace(/\s+/g, '-') // Replace spaces with hyphens
          .toLowerCase()
          .replace(/-+$/, '') // Remove trailing hyphens
      : '';

    // Store the original selection first
    const { from, to } = editor.state.selection;

    // Update the heading first
    editor
      .chain()
      .setNodeSelection(headingPos)
      .updateAttributes('heading', {
        id: headingTextContent,
      })
      .run();

    // Then separately handle the link at the original position
    editor
      .chain()
      .focus()
      .setTextSelection({ from, to })
      .extendMarkRange('link')
      .setLink({
        href: `#${headingTextContent}`,
        target: '_self',
      })
      .run();

    bubbleCleanup();
  };

  return (
    <Box
      data-testid="bubble-menu-link-headings-list"
      sx={commonStyles.container}
    >
      <List sx={{ height: '100%', overflow: 'auto' }}>
        {headings.map((heading, index) => (
          <ListItem
            data-testid={`heading-item-${index}`}
            key={heading.id}
            disablePadding
            sx={commonStyles.listItem}
          >
            <ListItemButton
              onClick={() => handleHeadingClick(heading.id)}
              sx={commonStyles.listButton}
            >
              <ListItemIcon>
                <FormatHeading sx={{ height: 16 }} />
              </ListItemIcon>
              <ListItemText
                primary={heading.textContent}
                sx={commonStyles.listItemText}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default LinkHeadingsList;
